// doctor-frontend/src/CitizenContext.js
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const CitizenCtx = createContext(null);

// ----- helpers -----
function getLoggedInUser() {
  try { return JSON.parse(localStorage.getItem('loggedInUser') || 'null'); } catch { return null; }
}
function getUserId() {
  const u = getLoggedInUser();
  return u && (u._id || u.id) ? String(u._id || u.id) : 'anon';
}
const keyFor = (uid, k) => `cmfm:${uid}:${k}`;

function readJSON(key) {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : null; } catch { return null; }
}

// legacy (pre-scoped) keys some pages still read
const LEGACY = {
  activeCitizen: 'activeCitizen',
  activeCitizenId: 'activeCitizenId',
  citizenId: 'citizenId',
  lastVisitedCitizenId: 'lastVisitedCitizenId',
  visitId: 'visitId',
  activeVisitId: 'activeVisitId',
};

function writeLegacyCitizen(c) {
  try {
    if (c && c.citizenId) {
      localStorage.setItem(LEGACY.activeCitizen, JSON.stringify(c));
      localStorage.setItem(LEGACY.activeCitizenId, c.citizenId);
      localStorage.setItem(LEGACY.citizenId, c.citizenId);
      localStorage.setItem(LEGACY.lastVisitedCitizenId, c.citizenId);
    } else {
      localStorage.removeItem(LEGACY.activeCitizen);
      localStorage.removeItem(LEGACY.activeCitizenId);
      localStorage.removeItem(LEGACY.citizenId);
    }
  } catch {}
}

function clearLegacyAll() {
  try {
    localStorage.removeItem(LEGACY.activeCitizen);
    localStorage.removeItem(LEGACY.activeCitizenId);
    localStorage.removeItem(LEGACY.citizenId);
    localStorage.removeItem(LEGACY.lastVisitedCitizenId);
    localStorage.removeItem(LEGACY.visitId);
    localStorage.removeItem(LEGACY.activeVisitId);
  } catch {}
}

// ----- provider -----
export function CitizenProvider({ children }) {
  const [userId, setUserId] = useState(getUserId());
  const [citizen, setCitizenState] = useState(null);

  const keys = useMemo(() => ({
    activeCitizen: keyFor(userId, 'activeCitizen'),
    activeCitizenId: keyFor(userId, 'activeCitizenId'),
    activeVisitId: keyFor(userId, 'activeVisitId'),
    lastVisitedCitizenId: keyFor(userId, 'lastVisitedCitizenId'),
  }), [userId]);

  const lastUserId = useRef(userId);

  // Hydrate scoped state (with legacy fallback + one-time migration)
  useEffect(() => {
    const scoped = readJSON(keys.activeCitizen);
    if (scoped && scoped.citizenId) {
      setCitizenState(scoped);
      // keep legacy in sync for old screens
      writeLegacyCitizen(scoped);
      return;
    }
    const legacy = readJSON(LEGACY.activeCitizen);
    if (legacy && legacy.citizenId) {
      setCitizenState(legacy);
      try {
        localStorage.setItem(keys.activeCitizen, JSON.stringify(legacy));
        localStorage.setItem(keys.activeCitizenId, legacy.citizenId);
        localStorage.setItem(keys.lastVisitedCitizenId, legacy.citizenId);
      } catch {}
      // keep as well for any still-legacy readers
      writeLegacyCitizen(legacy);
    } else {
      setCitizenState(null);
    }
    // eslint-disable-next-line
  }, [keys.activeCitizen, keys.activeCitizenId, keys.lastVisitedCitizenId]);

  // Detect login/user switch and clear memory + legacy globals to avoid “sticky citizen”
  useEffect(() => {
    const check = () => {
      const id = getUserId();
      if (id !== lastUserId.current) {
        lastUserId.current = id;
        setUserId(id);
        setCitizenState(null);
        // wipe both scoped and legacy on switch
        try {
          localStorage.removeItem(keys.activeCitizen);
          localStorage.removeItem(keys.activeCitizenId);
          localStorage.removeItem(keys.activeVisitId);
          localStorage.removeItem(keys.lastVisitedCitizenId);
        } catch {}
        clearLegacyAll();
      }
    };
    const onStorage = (e) => { if (e.key === 'loggedInUser') check(); };
    const onFocus = () => check();
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    const t = setInterval(check, 1200);
    return () => { window.removeEventListener('storage', onStorage); window.removeEventListener('focus', onFocus); clearInterval(t); };
    // eslint-disable-next-line
  }, []);

  // Writers
  const setCitizen = useCallback((c) => {
    setCitizenState(c || null);
    try {
      if (c?.citizenId) {
        localStorage.setItem(keys.activeCitizen, JSON.stringify(c));
        localStorage.setItem(keys.activeCitizenId, c.citizenId);
        localStorage.setItem(keys.lastVisitedCitizenId, c.citizenId);
      } else {
        localStorage.removeItem(keys.activeCitizen);
        localStorage.removeItem(keys.activeCitizenId);
        localStorage.removeItem(keys.lastVisitedCitizenId);
      }
    } catch {}
    // keep legacy in sync for older components
    writeLegacyCitizen(c || null);
  }, [keys.activeCitizen, keys.activeCitizenId, keys.lastVisitedCitizenId]);

  const setActiveVisitId = useCallback((visitId, { citizenId: cid } = {}) => {
    try {
      if (visitId) {
        localStorage.setItem(keys.activeVisitId, visitId);
        // legacy mirrors for older pages / ribbons
        localStorage.setItem(LEGACY.activeVisitId, visitId);
        localStorage.setItem(LEGACY.visitId, visitId);
        const effectiveCid = cid || (citizen && citizen.citizenId);
        if (effectiveCid) {
          localStorage.setItem(`activeVisitId:${effectiveCid}`, visitId);
        }
      } else {
        localStorage.removeItem(keys.activeVisitId);
        localStorage.removeItem(LEGACY.activeVisitId);
        localStorage.removeItem(LEGACY.visitId);
      }
    } catch {}
  }, [keys.activeVisitId, citizen]);

  const clearCitizen = useCallback(() => {
    setCitizenState(null);
    try {
      localStorage.removeItem(keys.activeCitizen);
      localStorage.removeItem(keys.activeCitizenId);
      localStorage.removeItem(keys.activeVisitId);
      localStorage.removeItem(keys.lastVisitedCitizenId);
    } catch {}
    clearLegacyAll();
  }, [keys.activeCitizen, keys.activeCitizenId, keys.activeVisitId, keys.lastVisitedCitizenId]);

  const value = useMemo(() => ({
    userId,
    citizen,
    setCitizen,
    clearCitizen,
    setActiveVisitId,
    storageKeys: keys,
  }), [userId, citizen, setCitizen, clearCitizen, setActiveVisitId, keys]);

  return <CitizenCtx.Provider value={value}>{children}</CitizenCtx.Provider>;
}

export const useCitizen = () => {
  const ctx = useContext(CitizenCtx);
  if (!ctx) throw new Error('useCitizen must be used within CitizenProvider');
  return ctx;
};